import { useState, useEffect, useRef } from "react";
import { FiActivity, FiLink2, FiBarChart2, FiBatteryCharging, FiSun, FiTool } from "react-icons/fi";
import { FaAngleUp } from "react-icons/fa";
import { MdOutlineEco } from "react-icons/md";
import { motion } from "framer-motion";
import PortfoliosSection from "./Portfolio";
import ExpertiseSection from "./Expertise";
import StatsSection from "./StatsSection";
import ClientsSection from "./Clients";
import Testimonial from "./TestimonialsSection";
import '../../../css/embal.css';
import PromotionSection from "./Promotion";
import headingBg from "../../../assets/header_bg.jpg";
import { Link } from "react-router-dom";
import AnnouncementBanner from "./AnnouncementBanner";
import airQuality from "../../../assets/airQuality.jpeg";
import waterQuality from "../../../assets/waterQuality.jpeg";
import pharma from "../../../assets/pharma.jpeg";
import industry from "../../../assets/industry.jpeg";
import iso from "../../../assets/iso.png";

export default function LandingPage() {
    const promoRef = useRef(null);
    const [showFloatingBanner, setShowFloatingBanner] = useState(false);
    const aboutUsRef = useRef(null);

    const handleScrollToPromo = () => {
        if (promoRef.current) {
            promoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (aboutUsRef.current) {
                const aboutUsTop = aboutUsRef.current.getBoundingClientRect().top;
                if (aboutUsTop <= 50) {
                    setShowFloatingBanner(true);
                } else {
                    setShowFloatingBanner(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const expertiseData = [
        {
            icon: <FiActivity size={50} />,
            title: "We Measure the Data",
            description: "We collect and analyze accurate data to gain valuable insights, ensuring precise decision-making and informed strategies.",
        },
        {
            icon: <FiLink2 size={50} />,
            title: "We Integrate the Insights",
            description: "We seamlessly combine data-driven insights with advanced technologies and processes, creating a cohesive and efficient system.",
        },
        {
            icon: <FiBarChart2 size={50} />,
            title: "We Innovate for a Smarter Future",
            description: "We leverage our integrated knowledge to develop cutting-edge solutions, driving progress and shaping a more intelligent and sustainable future.",
        },
    ];

    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) =>
                prevIndex === expertiseData.length - 1 ? 0 : prevIndex + 1
            );
        }, 10000); // Change story every 5 sec

        return () => clearInterval(interval); // Cleanup interval on unmount
    }, [expertiseData.length]);

    const portfoliosData = [
        {
            id: "Air Quality",
            title: "Air Quality",
            img: airQuality,
        },
        {
            id: "WaterQuality",
            title: "Water Quality",
            img: waterQuality,
        },
        {
            id: "Pharma and Bio-Tech",
            title: "Pharma-Biotech",
            img: pharma,
        },
        {
            id: "OccupationalHealthIndustrialHygiene",
            title: "Occupational Health & Industrial Hygiene",
            img: industry,
        },
    ];



    // State for the floating arrow visibility
    const [showArrow, setShowArrow] = useState(false);

    // Detect scroll position to show/hide the floating arrow
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 500) { // Show arrow after scrolling 500px
                setShowArrow(true);
            } else {
                setShowArrow(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <div className="min-h-screen bg-white text-black flex flex-col">
            {/* Main Content */}
            <div className="flex flex-col lg:flex-row flex-1 w-full gap-6">
                <div className="flex-1 flex flex-col">
                    {/* Heading Section */}
                    <div
                        className="flex flex-col items-center justify-center text-center px-4 sm:px-6 md:px-12 lg:px-20 xl:px-32"
                        style={{
                            backgroundImage: `url(${headingBg})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            minHeight: "100vh",
                            width: "100%",
                        }}
                    >
                        {/* Title */}
                        <h1
                            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-semibold leading-tight text-black max-w-8xl"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                            Empowering Environmental Monitoring with Innovation & Precision.
                        </h1>
                        {/* Subtitle */}
                        <p className="text-gray-600 mt-4 max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl text-sm sm:text-base md:text-lg" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: "400" }}>
                            Elevate your decision-making with real-time air, water, and weather monitoring solutions designed for compliance and efficiency.
                        </p>

                        {/* Buttons */}
                        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4 w-full sm:w-auto">
                            <button
                                onClick={handleScrollToPromo}
                                className="w-full sm:w-auto px-6 py-3 text-white font-semibold rounded-md transition duration-300 bg-green-500 hover:bg-green-600 flex items-center gap-2"
                            >
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                                </span>
                                Real-Time Polludrone Monitor
                            </button>


                            <Link to={"/contact"}>
                                <button className="w-full sm:w-auto px-6 py-3 font-semibold border-2 border-green-800 text-green-800 rounded-md transition duration-300 hover:bg-green-500 hover:text-white hover:border-green-500">
                                    Contact Sales
                                </button>
                            </Link>
                        </div>
                        <img
                            src={iso} // replace with your ISO logo URL
                            alt="ISO 9001:2015 Logo"
                            className="w-24 h-auto mb-2 mt-8" // adjust size as needed
                        />
                        <p
                            className=" max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl text-sm sm:text-base md:text-lg font-bold leading-tight text-black"
                            style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                            We are ISO 9001:2015 certified
                        </p>

                    </div>

                    <div ref={promoRef}>
                        <PromotionSection />
                    </div>

                    {/* About Us Section */}
                    <div ref={aboutUsRef} className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
                        {/* About Us Section */}
                        <div className="bg-white text-black py-10 sm:py-12 md:py-14 lg:py-16 ">
                            {/* Heading */}
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                                <span className="text-black">About</span> <span className="text-green-500">Us</span>
                            </h2>

                            {/* Description */}
                            <p className="text-sm sm:text-md md:text-lg text-gray-700 max-w-full mb-6 leading-relaxed">
                                Sun Teknolozy is a leader in environmental monitoring solutions, with over two decades of expertise in system integration, manufacturing, and IoT data connectivity.
                                We specialize in monitoring ambient air, industrial environments, marine and surface water, and pharmaceutical applications, ensuring precise and reliable data for
                                regulatory compliance and operational efficiency.
                            </p>

                            <p className="text-sm sm:text-md md:text-lg text-gray-700 max-w-full mb-8 leading-relaxed">
                                Our dedicated divisions provide cutting-edge solutions tailored to industry-specific needs, including Air Quality, Water Quality, Industrial Health & Hygiene, and
                                Pharma-Biotech. We uphold the highest standards of quality, reliability, and accuracy, delivering actionable data to support informed decision-making.
                            </p>

                            {/* Feature Icons Section */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
                                {/* Monitor Environment */}
                                <div className="flex flex-col items-center text-center">
                                    <FiSun size={48} className="text-green-500 mb-3" />
                                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Monitor Environment</h3>
                                    <p className="text-gray-600 text-sm sm:text-md leading-relaxed">
                                        Our advanced monitoring solutions deliver real-time environmental data, ensuring compliance, safety, and sustainability across industries.
                                    </p>
                                </div>

                                {/* Smart Technology */}
                                <div className="flex flex-col items-center text-center">
                                    <FiBatteryCharging size={48} className="text-green-500 mb-3" />
                                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Smart Technology</h3>
                                    <p className="text-gray-600 text-sm sm:text-md leading-relaxed">
                                        Our innovative IoT-based systems provide seamless automation, accurate data insights, and intelligent control for better decision-making.
                                    </p>
                                </div>

                                {/* Eco-Friendly Solutions */}
                                <div className="flex flex-col items-center text-center">
                                    <MdOutlineEco size={48} className="text-green-500 mb-3" />
                                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Eco-Friendly Solutions</h3>
                                    <p className="text-gray-600 text-sm sm:text-md leading-relaxed">
                                        We develop sustainable and energy-efficient solutions that minimize environmental impact and support green initiatives.
                                    </p>
                                </div>

                                {/* Service Support */}
                                <div className="flex flex-col items-center text-center">
                                    <FiTool size={48} className="text-green-500 mb-3" />
                                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Service Support</h3>
                                    <p className="text-gray-600 text-sm sm:text-md leading-relaxed">
                                        Our expert service engineers provide on-site and remote support, ensuring seamless operation and maintenance of your systems.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Other Sections */}
                    <div className='px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16'>
                        <ExpertiseSection expertiseData={expertiseData} currentIndex={currentIndex} />
                        <PortfoliosSection portfoliosData={portfoliosData} />
                        <StatsSection />
                        <ClientsSection />
                        <Testimonial />
                    </div>
                </div>
            </div>

            {/* Floating Arrow */}
            {showArrow && (
                <div
                    onClick={scrollToTop}
                    className="fixed bottom-10 right-10 p-2 bg-gray-500 text-white rounded-full cursor-pointer transition-all transform hover:scale-110 z-50 opacity-75 hover:opacity-100"
                    style={{
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", // subtle shadow to make it pop slightly
                    }}
                >
                    <FaAngleUp size={20} /> {/* Smaller FaAngleUp icon */}
                </div>
            )}

            {showFloatingBanner && (
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -50, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed top-0 left-0 w-full bg-green-500 text-white py-2 sm:py-3 px-4 sm:px-6 md:px-8 text-xs sm:text-sm md:text-base lg:text-lg font-semibold shadow-lg z-[60] text-center"
                >
                    <AnnouncementBanner />
                </motion.div>
            )}



        </div>
    );
}
